import type { TicketEarningsEvent } from '@pulsify/shared/dto'
import { Link } from '@tanstack/react-router'
import { CircleAlert, CircleCheck, Clock, Ticket } from 'lucide-react'
import { formatMoney, type PayoutTone, payoutState } from '~/entities/ticket/lib/ticket-earnings'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { Card } from '~/shared/ui/card'
import { eventDateLabel } from '../lib/earnings-list'

const COLUMNS = 'xl:grid-cols-[minmax(0,1fr)_6.5rem_8rem_8rem_12rem]'

interface EarningsListProps {
  rows: readonly TicketEarningsEvent[]
  currency: string
  locale: string
  now: Date
}

export function EarningsList({ rows, currency, locale, now }: EarningsListProps) {
  return (
    <Card className="overflow-hidden">
      <div
        aria-hidden
        className={cn(
          'hidden gap-4 border-border border-b px-5 py-2.5 font-medium text-muted-foreground text-xs xl:grid',
          COLUMNS,
        )}
      >
        <span>{m.earningsColumnEvent()}</span>
        <span className="text-right">{m.earningsSortSold()}</span>
        <span className="text-right">{m.earningsGross()}</span>
        <span className="text-right">{m.earningsColumnNet()}</span>
        <span>{m.earningsColumnPayout()}</span>
      </div>
      <ul className="divide-y divide-border">
        {rows.map((row) => (
          <li key={row.eventId}>
            <EarningsRow row={row} currency={currency} locale={locale} now={now} />
          </li>
        ))}
      </ul>
    </Card>
  )
}

function EarningsRow({
  row,
  currency,
  locale,
  now,
}: {
  row: TicketEarningsEvent
  currency: string
  locale: string
  now: Date
}) {
  const money = (cents: number) => formatMoney(cents, currency, locale)
  const payout = payoutState(row.payout, now, locale)

  return (
    <Link
      to="/dashboard/events/$eventId/tickets"
      params={{ eventId: row.eventId }}
      className={cn(
        'flex flex-col gap-3 px-4 py-4 outline-none transition-colors duration-150 ease-spring hover:bg-accent/60 focus-visible:bg-accent/60 sm:px-5 xl:grid xl:items-center xl:gap-4 xl:py-3',
        COLUMNS,
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        {row.coverImageUrl ? (
          <img
            src={row.coverImageUrl}
            alt=""
            loading="lazy"
            className="size-10 shrink-0 rounded-lg bg-muted object-cover"
          />
        ) : (
          <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted">
            <Ticket aria-hidden className="size-4 text-muted-foreground" />
          </span>
        )}
        <div className="min-w-0">
          <p className="truncate font-medium text-foreground text-sm">{row.title}</p>
          <p className="truncate text-muted-foreground text-xs">
            {eventDateLabel(row.startTime, locale, now)}
            {row.isCanceled ? ` · ${m.canceled()}` : ''}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 xl:contents">
        <Figure
          label={m.earningsSortSold()}
          value={new Intl.NumberFormat(locale).format(row.ticketsSold)}
          note={row.freeTickets > 0 ? m.earningsFreeCount({ count: row.freeTickets }) : null}
        />
        <Figure
          label={m.earningsGross()}
          value={row.sellsPaid ? money(row.grossCents) : '–'}
          note={
            row.refundedCents > 0 ? m.earningsRefunded({ amount: money(row.refundedCents) }) : null
          }
        />
        <Figure label={m.earningsColumnNet()} value={row.sellsPaid ? money(row.netCents) : '–'} />
      </div>
      <PayoutCell label={payout.label} tone={payout.tone} />
    </Link>
  )
}

function Figure({ label, value, note }: { label: string; value: string; note?: string | null }) {
  return (
    <div className="min-w-0 xl:text-right">
      <p className="truncate text-muted-foreground text-xs xl:sr-only">{label}</p>
      <p className="truncate font-medium text-foreground text-sm tabular-nums">{value}</p>
      {note ? <p className="text-muted-foreground text-xs leading-snug">{note}</p> : null}
    </div>
  )
}

const TONE_ICON = { planned: Clock, done: CircleCheck, alert: CircleAlert } as const

function PayoutCell({ label, tone }: { label: string; tone: PayoutTone }) {
  const Icon = tone === 'none' ? null : TONE_ICON[tone]
  return (
    <p
      className={cn(
        'flex min-w-0 items-start gap-1.5 text-sm',
        tone === 'alert' && 'text-destructive',
        tone === 'done' && 'text-foreground',
        (tone === 'planned' || tone === 'none') && 'text-muted-foreground',
      )}
    >
      {Icon ? <Icon aria-hidden className="mt-0.5 size-4 shrink-0" /> : null}
      <span className="min-w-0">{label}</span>
    </p>
  )
}
